import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";
import PriorityBadge from "./PriorityBadge";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { getStatusStyle, STATUSES } from "../lib/statusConfig";
import { getEffectiveDate } from "../lib/dateUtils";

const {
  FiThumbsUp, FiEdit2, FiTrash2, FiUser, FiX, FiChevronDown,
  FiChevronUp, FiMail, FiHash, FiTarget, FiAlertCircle, FiCheckCircle,
  FiXCircle, FiRotateCcw, FiFileText,
} = FiIcons;

const fieldStyle = {
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
};

const fieldClass =
  "w-full px-3 py-2.5 rounded-lg text-sm text-white placeholder-gray-500 outline-none focus:ring-1 focus:ring-violet-500 transition-all";

const labelClass = "block text-xs font-medium text-gray-400 mb-1.5";

/* ── Edit Modal ───────────────────────────────────────────── */
function EditModal({ feature, onSave, onClose }) {
  const [title, setTitle]       = useState(feature.feature || "");
  const [desc, setDesc]         = useState(feature.description || "");
  const [category, setCategory] = useState(feature.category || "");
  const [priority, setPriority] = useState((feature.priority || "medium").toLowerCase());
  const [status, setStatus]     = useState(feature.status || STATUSES.REQUESTED);
  const [closingComment, setClosingComment] = useState(feature.closing_comment || "");
  const [releaseNote, setReleaseNote] = useState(feature.release_note || "");
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  // Whether the closing-comment field should be shown at all: either the
  // feature is (or is being changed to) Closed, or it already has a
  // closing comment from a previous close (e.g. editing a reopened
  // feature shouldn't silently drop what was recorded before).
  const showClosingComment = status === STATUSES.CLOSED || !!feature.closing_comment;

  // Same idea for the release note: show it once the feature is (or is
  // being changed to) Released, or it already has one from a previous
  // release, so editing a released feature can actually change it —
  // unlike closing comments, this one is always optional (matches
  // ReleaseModal's behavior).
  const showReleaseNote = status === STATUSES.RELEASED || !!feature.release_note;

  const handleSave = async () => {
    if (!title.trim() || saving) return;
    // Closing comment is required whenever the feature is Closed — same
    // rule the dedicated Close button enforces via CloseModal. Without
    // this, switching status to Closed from here (or clearing the
    // comment on an already-closed feature) could leave a Closed feature
    // with no explanation.
    if (status === STATUSES.CLOSED && !closingComment.trim()) {
      setError('A closing comment is required when status is "Closed".');
      return;
    }
    setError("");
    setSaving(true);
    try {
      await onSave({
        feature: title.trim(),
        description: desc.trim(),
        category: category.trim(),
        priority,
        status,
        // Preserve/clear the closing comment based on the field shown
        // here, so editing a closed feature can actually change or clear
        // "why this was closed" instead of it being stuck forever.
        closing_comment: status === STATUSES.CLOSED ? closingComment.trim() : null,
        // Same treatment for the release note — editable here whenever
        // the feature is Released, cleared if status is changed away
        // from Released.
        release_note: status === STATUSES.RELEASED ? releaseNote.trim() : null,
      });
      // onSave (handleSaveEdit) closes the modal itself on success. If we
      // get here without throwing, there's nothing left to do.
    } catch (err) {
      // Surface the failure instead of leaving the button in a "did
      // nothing" state — this is what previously made Save look broken
      // whenever the update was rejected (e.g. an invalid status
      // transition or an RLS/database error).
      setError(err.message || "Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
      />

      {/* Panel */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 16 }}
        className="relative w-full max-w-lg rounded-2xl overflow-hidden"
        style={{
          background: "#13131c",
          border: "1px solid rgba(255,255,255,0.10)",
          boxShadow: "0 24px 80px rgba(0,0,0,0.7), 0 0 60px rgba(124,58,237,0.10)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
        >
          <h2 className="text-base font-semibold text-white">Edit Feature</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 hover:text-white transition-colors hover:bg-white/5"
          >
            <SafeIcon icon={FiX} className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
          {/* Title */}
          <div>
            <label className={labelClass}>Feature name *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={fieldClass}
              style={fieldStyle}
              placeholder="Feature title"
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label className={labelClass}>Description</label>
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              className={fieldClass + " resize-none"}
              style={fieldStyle}
              placeholder="Describe the feature..."
              rows={3}
            />
          </div>

          {/* Category */}
          <div>
            <label className={labelClass}>Category</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className={fieldClass}
              style={fieldStyle}
              placeholder="e.g. Integration, Backend, UI..."
            />
          </div>

          {/* Status + Priority row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Status</label>
              <div className="relative">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className={fieldClass + " appearance-none pr-8 cursor-pointer"}
                  style={fieldStyle}
                >
                  <option value={STATUSES.REQUESTED}>Requested</option>
                  <option value={STATUSES.PLANNED}>Planned</option>
                  <option value={STATUSES.IN_PROGRESS}>In Progress</option>
                  <option value={STATUSES.IN_BETA}>In Beta</option>
                  <option value={STATUSES.RELEASED}>Released</option>
                  <option value={STATUSES.CLOSED}>Closed</option>
                </select>
                <SafeIcon icon={FiChevronDown} className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className={labelClass}>Priority</label>
              <div className="relative">
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className={fieldClass + " appearance-none pr-8 cursor-pointer"}
                  style={fieldStyle}
                >
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
                <SafeIcon icon={FiChevronDown} className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Closing comment — shown once status is Closed (or already was),
              so editing a closed feature can actually update/clear "why
              this was closed" instead of that being stuck once set. */}
          {showClosingComment && (
            <div>
              <label className={labelClass}>
                Closing comment {status === STATUSES.CLOSED && <span className="text-red-400">*</span>}
              </label>
              <textarea
                value={closingComment}
                onChange={(e) => { setClosingComment(e.target.value); setError(""); }}
                className={fieldClass + " resize-none"}
                style={fieldStyle}
                placeholder='e.g. "This is not an actionable/valid feature request."'
                rows={3}
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Shown publicly under "Why this was closed" while the status is Closed.
              </p>
            </div>
          )}

          {/* Release note — shown once status is Released (or already was),
              so editing a released feature can actually update/clear it
              instead of that being stuck once set via the Release modal. */}
          {showReleaseNote && (
            <div>
              <label className={labelClass}>
                Release note <span className="text-gray-600">(optional)</span>
              </label>
              <textarea
                value={releaseNote}
                onChange={(e) => setReleaseNote(e.target.value)}
                className={fieldClass + " resize-none"}
                style={fieldStyle}
                placeholder="e.g. Shipped in v2.4 — available now in Beta for all users."
                rows={3}
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Shown publicly under "Release note" while the status is Released.
              </p>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mb-2 px-3 py-2 rounded-lg text-xs text-red-400" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.20)" }}>
            {error}
          </div>
        )}

        {/* Footer */}
        <div
          className="flex gap-3 px-6 py-4"
          style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}
        >
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:text-white transition-colors"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!title.trim() || saving}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all active:scale-[0.97] disabled:opacity-40"
            style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)", boxShadow: "0 0 20px rgba(124,58,237,0.30)" }}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

/* ── Close Request Modal ──────────────────────────────────── */
function CloseModal({ feature, onConfirm, onClose }) {
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleConfirm = async () => {
    if (!comment.trim()) {
      setError("A closing comment is required.");
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(comment.trim());
    } catch (err) {
      setError(err.message || "Failed to close request.");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 16 }}
        className="relative w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: "#13131c",
          border: "1px solid rgba(239,68,68,0.25)",
          boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
        }}
      >
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <h2 className="text-base font-semibold text-white flex items-center gap-2">
            <SafeIcon icon={FiXCircle} className="w-4 h-4 text-red-400" />
            Close Request
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-500 hover:text-white transition-colors hover:bg-white/5">
            <SafeIcon icon={FiX} className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 flex flex-col gap-4">
          <p className="text-sm text-gray-400">
            Closing <strong className="text-white">"{feature.feature}"</strong> moves it to the Closed tab.
            The requester's original request is preserved — please explain why it's being closed.
          </p>
          <div>
            <label className={labelClass}>Closing comment *</label>
            <textarea
              value={comment}
              onChange={(e) => { setComment(e.target.value); setError(""); }}
              className={fieldClass + " resize-none"}
              style={fieldStyle}
              placeholder='e.g. "This is not an actionable/valid feature request. Please add the exact feature that you would like."'
              rows={4}
              autoFocus
            />
          </div>
          {error && (
            <div className="px-3 py-2.5 rounded-lg text-sm text-red-300" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.20)" }}>
              {error}
            </div>
          )}
        </div>
        <div className="flex gap-3 px-6 py-4" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:text-white transition-colors"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting || !comment.trim()}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all active:scale-[0.97] disabled:opacity-40"
            style={{ background: "linear-gradient(135deg,#dc2626,#991b1b)", boxShadow: "0 0 20px rgba(220,38,38,0.30)" }}
          >
            {submitting ? "Closing…" : "Confirm Close"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

/* ── Release Modal ────────────────────────────────────────── */
function ReleaseModal({ feature, onConfirm, onClose }) {
  const [note, setNote] = useState(feature.release_note || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm(note.trim());
    } catch (err) {
      setError(err.message || "Failed to mark as released.");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 16 }}
        className="relative w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: "#13131c",
          border: "1px solid rgba(34,197,94,0.25)",
          boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
        }}
      >
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <h2 className="text-base font-semibold text-white flex items-center gap-2">
            <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-400" />
            Mark as Released
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-500 hover:text-white transition-colors hover:bg-white/5">
            <SafeIcon icon={FiX} className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 flex flex-col gap-4">
          <p className="text-sm text-gray-400">
            This moves <strong className="text-white">"{feature.feature}"</strong> to the public Released tab.
          </p>
          <div>
            <label className={labelClass}>Release note <span className="text-gray-600">(optional)</span></label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className={fieldClass + " resize-none"}
              style={fieldStyle}
              placeholder="e.g. Shipped in v2.4 — available now in Beta for all users."
              rows={3}
              autoFocus
            />
          </div>
          {error && (
            <div className="px-3 py-2.5 rounded-lg text-sm text-red-300" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.20)" }}>
              {error}
            </div>
          )}
        </div>
        <div className="flex gap-3 px-6 py-4" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:text-white transition-colors"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all active:scale-[0.97] disabled:opacity-40"
            style={{ background: "linear-gradient(135deg,#16a34a,#15803d)", boxShadow: "0 0 20px rgba(22,163,74,0.30)" }}
          >
            {submitting ? "Saving…" : "Confirm Release"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

/* ── Internal Notes (admin-only) ─────────────────────────── */
function InternalNotesModal({ feature, onLoad, onSave, onClose }) {
  const [notes, setNotes] = useState("");
  const [loadingNote, setLoadingNote] = useState(true);
  const [savedAt, setSavedAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    let mounted = true;
    onLoad(feature.id)
      .then((result) => {
        if (!mounted) return;
        setNotes(result.notes || "");
        setSavedAt(result.updated_at || null);
      })
      .catch((err) => {
        if (mounted) setError(err.message || "Failed to load internal notes.");
      })
      .finally(() => {
        if (mounted) setLoadingNote(false);
      });
    return () => {
      mounted = false;
    };
  }, [feature.id]);

  const handleSave = async () => {
    setSubmitting(true);
    setError("");
    try {
      const result = await onSave(feature.id, notes);
      setSavedAt(result.updated_at || new Date().toISOString());
    } catch (err) {
      setError(err.message || "Failed to save internal notes.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 16 }}
        className="relative w-full max-w-lg rounded-2xl overflow-hidden"
        style={{
          background: "#13131c",
          border: "1px solid rgba(99,102,241,0.25)",
          boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
        }}
      >
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <h2 className="text-base font-semibold text-white flex items-center gap-2">
            <SafeIcon icon={FiFileText} className="w-4 h-4 text-indigo-400" />
            Internal Notes
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-500 hover:text-white transition-colors hover:bg-white/5">
            <SafeIcon icon={FiX} className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 flex flex-col gap-4">
          <p className="text-xs text-gray-500">
            Only visible to admins — never shown to the requester or the public roadmap.
          </p>
          <div>
            <label className={labelClass}>Notes for "{feature.feature}"</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={fieldClass + " resize-none"}
              style={fieldStyle}
              placeholder={loadingNote ? "Loading…" : "e.g. Internal context, links, discussion history, decisions made..."}
              rows={6}
              disabled={loadingNote}
              autoFocus
            />
            {savedAt && (
              <p className="text-[11px] text-gray-600 mt-1.5">
                Last saved {new Date(savedAt).toLocaleString()}
              </p>
            )}
          </div>
          {error && (
            <div className="px-3 py-2.5 rounded-lg text-sm text-red-300" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.20)" }}>
              {error}
            </div>
          )}
        </div>
        <div className="flex gap-3 px-6 py-4" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:text-white transition-colors"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            Close
          </button>
          <button
            onClick={handleSave}
            disabled={submitting || loadingNote}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all active:scale-[0.97] disabled:opacity-40"
            style={{ background: "linear-gradient(135deg,#6366f1,#4338ca)", boxShadow: "0 0 20px rgba(99,102,241,0.30)" }}
          >
            {submitting ? "Saving…" : "Save Note"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

/* ── Delete confirmation ──────────────────────────────────── */
function DeleteConfirm({ feature, onConfirm, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 16 }}
        className="relative w-full max-w-sm rounded-2xl overflow-hidden p-6"
        style={{ background: "#13131c", border: "1px solid rgba(239,68,68,0.25)", boxShadow: "0 24px 80px rgba(0,0,0,0.7)" }}
      >
        <h2 className="text-base font-semibold text-white mb-2">Delete this feature?</h2>
        <p className="text-sm text-gray-400 mb-5">
          This permanently deletes <strong className="text-white">"{feature.feature}"</strong>. This cannot be undone.
          Consider using "Close" instead if you want to keep a record of why this request was declined.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:text-white transition-colors"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all active:scale-[0.97]"
            style={{ background: "linear-gradient(135deg,#dc2626,#991b1b)", boxShadow: "0 0 20px rgba(220,38,38,0.30)" }}
          >
            Delete
          </button>
        </div>
      </motion.div>
    </div>
  );
}

/* ── FeatureCard ──────────────────────────────────────────── */
const FeatureCard = ({
  feature,
  onUpvote,
  onUpdate,
  onDelete,
  onClose: onCloseFeature,
  onRelease,
  onReopen,
  onLoadInternalNote,
  onSaveInternalNote,
  isAdmin,
}) => {
  const [votedFeatures, setVotedFeatures] = useLocalStorage("votedFeatures", []);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [actionError, setActionError] = useState("");

  const hasVoted = votedFeatures.includes(feature.id);
  const s = getStatusStyle(feature.status);

  // discord_username / email only ever exist on `feature` for admin
  // sessions (useFeatures only fetches/merges feature_requester_contact
  // when isAdmin is true), but gate on isAdmin here too as defense in
  // depth so a public session can never render them even if that changes.
  const hasExtraDetails =
    feature.use_case ||
    feature.justification ||
    (isAdmin && (feature.discord_username || feature.email));

  // Byline shown at the bottom of the card, admin sessions only. Falls back
  // to the Discord handle so admins still see who filed a request now that
  // it's no longer copied into the public `requested_by` column. Reading
  // discord_username is safe here because useFeatures only ever merges it
  // onto `feature` when isAdmin is true — but the isAdmin check at the
  // render site is what actually guarantees a public session can't show it.
  const requesterLabel =
    feature.requested_by || feature.requestedBy || feature.discord_username || null;

  const handleUpvote = async () => {
    if (!hasVoted) {
      try {
        await onUpvote(feature.id);
        setVotedFeatures([...votedFeatures, feature.id]);
      } catch (err) {
        console.error("Error upvoting:", err);
      }
    }
  };

  const handleSaveEdit = async (updates) => {
    // Let a thrown error propagate back to EditModal so it can show the
    // failure and stay open, instead of onUpdate throwing here with no
    // catch — which previously left the modal stuck with no feedback and
    // looked exactly like the Save button "not working".
    await onUpdate(feature.id, updates);
    setShowEditModal(false);
  };

  const handleConfirmClose = async (comment) => {
    await onCloseFeature(feature.id, comment);
    setShowCloseModal(false);
  };

  const handleConfirmRelease = async (note) => {
    await onRelease(feature.id, note);
    setShowReleaseModal(false);
  };

  const handleDelete = async () => {
    try {
      await onDelete(feature.id);
    } catch (err) {
      setActionError(err.message || "Failed to delete.");
    }
    setShowDeleteConfirm(false);
  };

  const handleReopen = async () => {
    try {
      await onReopen(feature.id);
    } catch (err) {
      setActionError(err.message || "Failed to reopen.");
    }
  };

  return (
    <>
      <motion.div
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="flex flex-col rounded-xl transition-all"
        style={{
          background: isAdmin ? "rgba(124,58,237,0.04)" : "rgba(255,255,255,0.035)",
          border: isAdmin
            ? "1px solid rgba(124,58,237,0.18)"
            : "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 2px 20px rgba(0,0,0,0.3)",
        }}
        whileHover={{
          borderColor: isAdmin ? "rgba(124,58,237,0.35)" : "rgba(124,58,237,0.25)",
          boxShadow: "0 4px 30px rgba(124,58,237,0.08)",
        }}
      >
        {/* ── Card body ── */}
        <div className="p-5">
          {/* Top row */}
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-white text-base leading-snug">
                  {feature.feature || "Feature"}
                </h3>
                <PriorityBadge priority={feature.priority || "medium"} />
              </div>
              <p className="text-gray-400 text-sm mt-1.5 leading-relaxed">
                {feature.description}
              </p>
            </div>

            {/* Category + status (users only — admins have status in the toolbar) */}
            {!isAdmin && (
              <div className="flex flex-col items-end gap-2 shrink-0 mt-0.5">
                {feature.category && (
                  <span
                    className="text-xs font-medium px-2.5 py-1 rounded-full"
                    style={{ background: "rgba(124,58,237,0.15)", border: "1px solid rgba(124,58,237,0.25)", color: "#c4b5fd" }}
                  >
                    {feature.category}
                  </span>
                )}
                {feature.status && (
                  <span
                    className="text-xs font-medium px-2.5 py-1 rounded-full"
                    style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.text }}
                  >
                    {s.label}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Release note (visible to everyone once released) */}
          {feature.status === "Released" && feature.release_note && (
            <div
              className="mt-3 p-3 rounded-lg text-sm"
              style={{ background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.18)" }}
            >
              <p className="text-xs font-medium text-green-400 mb-1">Release note</p>
              <p className="text-green-100/80 text-sm leading-relaxed">{feature.release_note}</p>
            </div>
          )}

          {/* Closing comment (visible to everyone once closed) */}
          {feature.status === "Closed" && feature.closing_comment && (
            <div
              className="mt-3 p-3 rounded-lg text-sm"
              style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.18)" }}
            >
              <p className="text-xs font-medium text-red-400 mb-1">Why this was closed</p>
              <p className="text-red-100/80 text-sm leading-relaxed">{feature.closing_comment}</p>
            </div>
          )}

          {/* Admin-only status badge row */}
          {isAdmin && (
            <div className="flex items-center gap-2 mt-3">
              {feature.category && (
                <span
                  className="text-xs font-medium px-2.5 py-1 rounded-full"
                  style={{ background: "rgba(124,58,237,0.15)", border: "1px solid rgba(124,58,237,0.25)", color: "#c4b5fd" }}
                >
                  {feature.category}
                </span>
              )}
              <span
                className="text-xs font-medium px-2.5 py-1 rounded-full"
                style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.text }}
              >
                {s.label}
              </span>
            </div>
          )}

          {/* Requester details (use case, discord, email, justification) */}
          {hasExtraDetails && (
            <div className="mt-3">
              <button
                onClick={() => setShowDetails((v) => !v)}
                className="flex items-center gap-1.5 text-xs font-medium text-violet-400 hover:text-violet-300 transition-colors"
              >
                <SafeIcon icon={showDetails ? FiChevronUp : FiChevronDown} className="w-3.5 h-3.5" />
                {showDetails ? "Hide" : "Show"} requester details
              </button>

              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div
                      className="mt-2 p-3 rounded-lg space-y-2.5 text-sm"
                      style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}
                    >
                      {feature.use_case && (
                        <div className="flex gap-2">
                          <SafeIcon icon={FiTarget} className="w-3.5 h-3.5 text-gray-500 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs text-gray-500 mb-0.5">Use case</p>
                            <p className="text-gray-300 leading-relaxed">{feature.use_case}</p>
                          </div>
                        </div>
                      )}
                      {feature.justification && (
                        <div className="flex gap-2">
                          <SafeIcon icon={FiAlertCircle} className="w-3.5 h-3.5 text-orange-400 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs text-orange-400 mb-0.5">
                              Why {feature.priority}?
                            </p>
                            <p className="text-gray-300 leading-relaxed">{feature.justification}</p>
                          </div>
                        </div>
                      )}
                      {isAdmin && (feature.discord_username || feature.email) && (
                        <div className="flex flex-wrap gap-x-5 gap-y-1.5 pt-1">
                          {feature.discord_username && (
                            <span className="flex items-center gap-1.5 text-xs text-gray-400">
                              <SafeIcon icon={FiHash} className="w-3 h-3" /> {feature.discord_username}
                            </span>
                          )}
                          {feature.email && (
                            <span className="flex items-center gap-1.5 text-xs text-gray-400">
                              <SafeIcon icon={FiMail} className="w-3 h-3" /> {feature.email}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Bottom row */}
          <div className="flex justify-between items-end mt-4 pt-4" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <button
              onClick={handleUpvote}
              disabled={hasVoted}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                hasVoted ? "cursor-not-allowed opacity-50" : "hover:scale-105 active:scale-95"
              }`}
              style={{
                background: hasVoted ? "rgba(255,255,255,0.05)" : "rgba(124,58,237,0.15)",
                border: `1px solid ${hasVoted ? "rgba(255,255,255,0.08)" : "rgba(124,58,237,0.30)"}`,
                color: hasVoted ? "#6b7280" : "#c4b5fd",
              }}
            >
              <SafeIcon icon={FiThumbsUp} className="w-3.5 h-3.5" />
              <span>{feature.upvotes || 0}</span>
            </button>

            <div className="text-right space-y-0.5">
              {/* Admin-only byline. `requested_by` is a plain column on
                  `features`, so it ships to every visitor via the public
                  select("*") and Realtime — it must never hold a Discord
                  handle. createFeature used to copy discord_username into
                  it, which is what was showing up on public cards; that's
                  gone, so it's null on new submissions.

                  The handle itself is still collected and stored, just in
                  the admin-only feature_requester_contact table. It's
                  merged onto `feature` for admin sessions only, so reading
                  it here keeps the byline working for admins without
                  putting it anywhere public. */}
              {isAdmin && requesterLabel && (
                <p className="text-xs text-gray-600 flex items-center justify-end gap-1">
                  <SafeIcon icon={FiUser} className="w-3 h-3" />
                  {requesterLabel}
                </p>
              )}
              {(() => {
                const effectiveDate = getEffectiveDate(feature);
                const isUpdated = feature.updated_at && feature.updated_at !== feature.created_at;
                return (
                  <p className="text-xs text-gray-600">
                    {isUpdated ? "Updated " : ""}
                    {effectiveDate ? effectiveDate.toLocaleDateString() : "—"}
                  </p>
                );
              })()}
            </div>
          </div>

          {actionError && (
            <div className="mt-3 px-3 py-2 rounded-lg text-xs text-red-300" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.20)" }}>
              {actionError}
            </div>
          )}
        </div>

        {/* ── Admin toolbar ── */}
        {isAdmin && (
          <div
            className="flex items-center flex-wrap justify-end gap-2 px-5 py-3 rounded-b-xl"
            style={{ background: "rgba(124,58,237,0.08)", borderTop: "1px solid rgba(124,58,237,0.15)" }}
          >
            {feature.status === "Closed" ? (
              <button
                onClick={handleReopen}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-300 hover:text-white transition-all"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}
              >
                <SafeIcon icon={FiRotateCcw} className="w-3.5 h-3.5" />
                Reopen
              </button>
            ) : (
              <button
                onClick={() => setShowCloseModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-400 hover:text-white transition-all"
                style={{ background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.20)" }}
              >
                <SafeIcon icon={FiXCircle} className="w-3.5 h-3.5" />
                Close
              </button>
            )}

            {feature.status !== "Released" && (
              <button
                onClick={() => setShowReleaseModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-green-400 hover:text-white transition-all"
                style={{ background: "rgba(34,197,94,0.10)", border: "1px solid rgba(34,197,94,0.20)" }}
              >
                <SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5" />
                Release
              </button>
            )}

            <button
              onClick={() => setShowNotesModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-indigo-300 hover:text-white transition-all"
              style={{ background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.25)" }}
            >
              <SafeIcon icon={FiFileText} className="w-3.5 h-3.5" />
              Internal Notes
            </button>
            <button
              onClick={() => setShowEditModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-violet-300 hover:text-white transition-all"
              style={{ background: "rgba(124,58,237,0.15)", border: "1px solid rgba(124,58,237,0.25)" }}
            >
              <SafeIcon icon={FiEdit2} className="w-3.5 h-3.5" />
              Edit
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-400 hover:text-white transition-all"
              style={{ background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.20)" }}
            >
              <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
              Delete
            </button>
          </div>
        )}
      </motion.div>

      {/* Modals — rendered outside card so they're not clipped */}
      <AnimatePresence>
        {showEditModal && (
          <EditModal feature={feature} onSave={handleSaveEdit} onClose={() => setShowEditModal(false)} />
        )}
        {showCloseModal && (
          <CloseModal feature={feature} onConfirm={handleConfirmClose} onClose={() => setShowCloseModal(false)} />
        )}
        {showReleaseModal && (
          <ReleaseModal feature={feature} onConfirm={handleConfirmRelease} onClose={() => setShowReleaseModal(false)} />
        )}
        {showDeleteConfirm && (
          <DeleteConfirm feature={feature} onConfirm={handleDelete} onClose={() => setShowDeleteConfirm(false)} />
        )}
        {showNotesModal && (
          <InternalNotesModal
            feature={feature}
            onLoad={onLoadInternalNote}
            onSave={onSaveInternalNote}
            onClose={() => setShowNotesModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default FeatureCard;
